import { getFileExtension, getFileType } from 'utils/filesUtils';
import { removeHtmlTags } from 'utils/utils';

/**
 * Get file type from file name
 */
function findType(item) {
  return getFileType(getFileExtension(item?.file_name));
}

/**
 * Helper function to get res_id from item (supports both old and new format)
 * @param {Object} item - Item from API response
 * @returns {string|null} - Resource ID
 */
function getResId(item) {
  return (
    item?.res_type_id ||
    item?.res_id ||
    (item?.sk ? item.sk.split('#').pop() : null) ||
    (item?.pk ? item.pk.split('#').pop() : null) ||
    null
  );
}

/**
 * Helper function to check if item is valid (has at least some identifying information)
 * @param {Object} item - Item from API response
 * @returns {boolean} - Whether item is valid
 */
function isValidItem(item) {
  if (!item || typeof item !== 'object') return false;
  // Item is valid if it has any identifying information
  // Be very lenient to avoid filtering out valid items
  // Check for res_id, pk, sk, title, or any field that suggests it's a valid result
  return !!(
    (
      getResId(item) ||
      item?.title ||
      item?.pk ||
      item?.sk ||
      item?.res_type_id ||
      item?.res_type ||
      item?.subtitle ||
      item?.description ||
      Object.keys(item || {}).length > 0
    ) // If it has any keys, consider it valid
  );
}

/**
 * Helper function to get highlights from item (supports both old and new format)
 * @param {Object} item - Item from API response
 * @returns {Object} - Highlights object
 */
function getHighlights(item) {
  return item?._highlights || item?.highlight || {};
}

/**
 * Helper function to get highlighted or plain text value
 * For global-search-v2, title/subtitle/description fields already contain <mark> tags
 * Prioritizes direct field value first, then falls back to _highlights if needed
 * @param {Object} item - Item from API response
 * @param {string} field - Field name (title, subtitle, description)
 * @returns {string} - Highlighted or plain text value
 */
function getHighlightedValue(item, field) {
  // For global-search-v2, the title/subtitle/description fields already contain <mark> tags
  // So prioritize the direct field value first
  if (item[field]) {
    return item[field];
  }
  // Fall back to _highlights if direct field is not available
  const highlights = getHighlights(item);
  return highlights[field] || '';
}

/**
 * Maps quick-search API response to component-expected format
 * Supports both old format (res_id) and new format (res_type_id) for quick-search-v2
 * @param {Object} data - Quick search API response data
 * @returns {Object} - Mapped results grouped by resource type
 */
export function mapQuickSearchResults(data) {
  if (!data) {
    console.warn('mapQuickSearchResults: No data provided');
    return {};
  }

  const quickSearchResults = {};

  // Debug: Log what we're processing
  const dataKeys = Object.keys(data || {});
  const hasArrays = dataKeys.filter((key) => Array.isArray(data[key]) && data[key].length > 0);
  if (hasArrays.length === 0 && dataKeys.length > 0) {
    console.warn('mapQuickSearchResults: Data provided but no non-empty arrays found', {
      dataKeys,
      sampleValues: dataKeys.slice(0, 5).reduce((acc, key) => {
        acc[key] = Array.isArray(data[key]) ? `Array(${data[key].length})` : typeof data[key];
        return acc;
      }, {}),
    });
  }

  // Map contact -> user (for component compatibility)
  if (data.contact && Array.isArray(data.contact)) {
    quickSearchResults.user = data.contact
      .filter((item) => isValidItem(item))
      .map((item) => {
        const resId = getResId(item);
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || item?.title || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        // Extract plain text for first_name/last_name (remove mark tags if present)
        const plainTitle = item.title || '';
        // Extract name parts from title if individual fields are not available
        const titleParts = plainTitle ? plainTitle.split(' ').filter(Boolean) : [];
        return {
          ...item, // Preserve all original item fields
          res_type: 'user',
          contact_id: resId,
          user_id: resId,
          pk: item.pk,
          sk: item.sk,
          contact_type: item?.contact_type || 'person',
          first_name: item?.first_name || (titleParts.length > 0 ? titleParts[0] : '') || '',
          last_name: item?.last_name || (titleParts.length > 1 ? titleParts.slice(1).join(' ') : '') || '',
          title: title,
          subtitle: subtitle,
          description: description,
          highlights: highlights,
        };
      });
  }

  // Map case results (separate cases and leads)
  if (data.case && Array.isArray(data.case)) {
    const cases = [];
    const leads = [];
    data.case
      .filter((item) => isValidItem(item))
      .forEach((item) => {
        const resId = getResId(item);
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || item?.case_description?.case_name || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        const meta = item.meta || '';
        const mappedItem = {
          ...item, // Preserve all original item fields
          res_type: 'case',
          case_id: resId,
          pk: item.pk,
          sk: item.sk,
          case_description: {
            ...item?.case_description,
            case_name: title,
            case_number: item?.case_description?.case_number || meta || '',
          },
          is_lead: item?.is_lead || false,
          title: title,
          subtitle: subtitle,
          description: description,
          highlights: highlights,
        };
        if (mappedItem.is_lead) {
          leads.push(mappedItem);
        } else {
          cases.push(mappedItem);
        }
      });
    if (cases.length > 0) quickSearchResults.case = cases;
    if (leads.length > 0) {
      // Add leads to case array but mark as leads
      quickSearchResults.case = [...(quickSearchResults.case || []), ...leads];
    }
  }

  // Map lead results if separate (res_type: "lead")
  if (data.lead && Array.isArray(data.lead)) {
    const leads = data.lead
      .filter((item) => isValidItem(item))
      .map((item) => {
        const resId = getResId(item);
        const highlights = getHighlights(item);
        // Use plain title first (from item.title), then highlighted, then fallback
        // For leads, the title field contains the case name
        // Strip HTML tags from title to get plain text version
        const rawTitle = item?.title || '';
        const plainTitle = removeHtmlTags(rawTitle) || '';
        const highlightedTitle = getHighlightedValue(item, 'title') || '';
        const title = plainTitle || removeHtmlTags(highlightedTitle) || item?.case_description?.case_name || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        const meta = item.meta || '';
        const mappedLead = {
          ...item, // Preserve all original item fields
          res_type: item?.res_type === 'lead' ? 'lead' : 'case', // Preserve res_type: 'lead' if present
          case_id: resId || item?.case_id || item?.res_type_id,
          pk: item.pk,
          sk: item.sk,
          case_description: {
            ...item?.case_description,
            case_name: plainTitle || removeHtmlTags(title) || '', // Use plain title for case_name (no HTML tags)
            case_number: item?.case_description?.case_number || meta || '',
          },
          is_lead: true,
          title: highlightedTitle || plainTitle || title, // Use highlighted title for display (with HTML tags for highlighting)
          subtitle: subtitle,
          description: description,
          highlights: highlights,
        };
        return mappedLead;
      });
    if (leads.length > 0) {
      // Store leads in separate 'lead' key instead of adding to case array
      quickSearchResults.lead = leads;
    }
  }

  // Map file_system_entry
  if (data.file_system_entry && Array.isArray(data.file_system_entry)) {
    quickSearchResults.file_system_entry = data.file_system_entry
      .filter((item) => isValidItem(item))
      .map((item) => {
        const resId = getResId(item);
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || item?.display_name || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        return {
          ...item, // Preserve all original item fields
          res_type: 'file_system_entry',
          file_path: item?.file_path || item?.object_key || '',
          object_id: resId,
          file_id: resId,
          document_id: resId,
          display_name: title,
          file_name: item?.file_name || item.title || '',
          title: title,
          subtitle: subtitle,
          description: description,
          highlights: highlights,
          pk: item.pk,
          sk: item.sk,
        };
      });
  }

  // Map event
  if (data.event && Array.isArray(data.event)) {
    quickSearchResults.event = data.event
      .filter((item) => isValidItem(item))
      .map((item) => {
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        return {
          ...item, // Preserve all original item fields
          res_type: 'event',
          pk: item.pk,
          sk: item.sk,
          title: title,
          subtitle: subtitle,
          description: description,
          case_name: item?.case_name || '',
          highlights: highlights,
        };
      });
  }

  // Map message
  if (data.message && Array.isArray(data.message)) {
    quickSearchResults.message = data.message
      .filter((item) => isValidItem(item))
      .map((item) => {
        const resId = getResId(item);
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        return {
          ...item, // Preserve all original item fields
          res_type: 'message',
          message_thread_id: resId,
          subject: title,
          title: title,
          subtitle: subtitle,
          description: description,
          highlights: highlights,
          pk: item.pk,
          sk: item.sk,
        };
      });
  }

  // Map email_thread (legacy API may still return text_message, but we'll map text messages separately)
  if (data.email_thread && Array.isArray(data.email_thread)) {
    quickSearchResults.email_thread = data.email_thread
      .filter((item) => isValidItem(item))
      .map((item) => {
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || item?.body || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        return {
          ...item, // Preserve all original item fields
          res_type: 'email_thread',
          contact_id: item?.contact_id,
          to: item?.to || item?.To,
          body: title,
          title: title,
          subtitle: subtitle,
          description: description,
          highlights: highlights,
          pk: item.pk,
          sk: item.sk,
        };
      });
  }

  // Map text_message_thread
  if (data.text_message_thread && Array.isArray(data.text_message_thread)) {
    quickSearchResults.text_message_thread = data.text_message_thread
      .filter((item) => isValidItem(item))
      .map((item) => {
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || item?.body || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        return {
          ...item, // Preserve all original item fields
          res_type: 'text_message_thread',
          contact_id: item?.contact_id,
          to: item?.to || item?.To,
          body: title,
          title: title,
          subtitle: subtitle,
          description: description,
          highlights: highlights,
          pk: item.pk,
          sk: item.sk,
        };
      });
  }

  // Map text_message_reply
  if (data.text_message_reply && Array.isArray(data.text_message_reply)) {
    quickSearchResults.text_message_reply = data.text_message_reply
      .filter((item) => isValidItem(item))
      .map((item) => {
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || item?.body || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        return {
          ...item, // Preserve all original item fields
          res_type: 'text_message_reply',
          contact_id: item?.contact_id,
          to: item?.to || item?.To,
          body: title,
          title: title,
          subtitle: subtitle,
          description: description,
          highlights: highlights,
          pk: item.pk,
          sk: item.sk,
        };
      });
  }

  // Map task
  if (data.task && Array.isArray(data.task)) {
    quickSearchResults.task = data.task
      .filter((item) => isValidItem(item))
      .map((item) => {
        const resId = getResId(item);
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        const meta = item.meta || '';
        return {
          ...item, // Preserve all original item fields
          res_type: 'task',
          task_id: resId,
          task_name: title,
          title: title,
          subtitle: subtitle,
          description: description,
          case_name: item?.case_name || meta || '',
          highlights: highlights,
          pk: item.pk,
          sk: item.sk,
        };
      });
  }

  // Map case_note - check for both 'case_note' (underscore) and 'case-note' (hyphen) keys
  const caseNoteData = data.case_note || data['case-note'];
  if (caseNoteData && Array.isArray(caseNoteData)) {
    const mappedCaseNotes = caseNoteData
      .filter((item) => isValidItem(item))
      .map((item) => {
        const resId = getResId(item);
        const highlights = getHighlights(item);
        // Use highlighted title/subtitle/description if available, otherwise use plain versions
        const title = getHighlightedValue(item, 'title') || '';
        const subtitle = getHighlightedValue(item, 'subtitle') || '';
        const description = getHighlightedValue(item, 'description') || '';
        return {
          ...item, // Preserve all original item fields
          res_type: 'case-note',
          case_note_id: resId,
          case_id: item?.case_id,
          title: title,
          subtitle: subtitle,
          description: description,
          highlights: highlights,
          pk: item.pk,
          sk: item.sk,
        };
      });
    if (mappedCaseNotes.length > 0) {
      quickSearchResults['case-note'] = mappedCaseNotes;
    }
  }

  return quickSearchResults;
}

/**
 * Maps global-search content API response to component-expected format
 * @param {Object} data - Global search content API response data
 * @returns {Array} - Mapped file results
 */
export function mapGlobalSearchContentResults(data) {
  if (!data) return [];

  // Support both old and new response shapes:
  // Old: res.data.documents.buckets
  // New: res.data.results.documents.buckets
  const documentsRoot = data?.documents || data?.results?.documents;
  const buckets = documentsRoot?.buckets || [];

  return buckets.map((v) => {
    const source = v?.top_hit?.hits?.hits?.[0]?._source || {};
    return {
      ...source,
      file_path: source?.object_key || source?.file_path,
      object_id: source?.document_id || source?.file_id || source?.object_id,
      type: findType(source),
    };
  });
}
